/**
 * useKb — the single owner-generic KB hook. Replaces useAssetKbFiles +
 * useUseCaseKbFiles (both retired). Read is the owner-generic GetKbTree
 * (folders[] + entries[] + overview); writes go through kbWriteOps (pure pass-through
 * to the owner-generic ops, keyed by {ownerType, ownerId}).
 *
 * The tree is built CLIENT-SIDE from the flat GetKbTree output: entries group under
 * folders by folderId (absent = root). Overview is returned separately (rendered as a
 * pinned row). The backend seeds preset folders (Awareness / Interest / Purchase) per
 * owner; upload can target a folder (folderId) or root.
 *
 * Presigned-URL rules are unchanged from the old hooks: the browser PUT MUST carry
 * Content-Type equal to the signed contentType and nothing else (403 SignatureDoesNotMatch
 * otherwise); download/preview URLs are bearer tokens used once and never logged.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getIvtClient,
  GetKbTreeCommand,
  type KbEntry,
  type KbFolder,
  type KbOwnerType,
  type KbContentType,
} from '../../../client/IvtHubClient';
import { kbWriteOps, KbDownloadDisposition, type KbOwner } from '../../../client/kbWriteOps';
import { contentTypeForFile, isEditableFile, KB_MAX_FILE_BYTES, KB_MAX_FILE_LABEL } from './kbFormats';
import { DENIAL_HINTS, denialHint, denialKindOf, type DenialKind } from '../../../utils/authorization';

/** One file's upload failure. `denialKind` set = an authorization denial, whose hint the
 *  batch states once rather than per file. */
interface UploadFailure {
  message: string;
  denialKind?: DenialKind;
}

/** A folder plus the entries that live in it (built client-side from GetKbTree). */
export interface KbTreeFolder {
  folder: KbFolder;
  entries: KbEntry[];
}

/** The owner-generic KB panel surface. The panel consumes KbEntry (not KbFile). */
export interface UseKb {
  /** Root-level entries (folderId absent) — files + urls not in any folder. */
  rootEntries: KbEntry[];
  /** Folders with their entries, in server order. Empty until CR-3 seeds folders. */
  folders: KbTreeFolder[];
  /** The Overview doc (absent = never saved → the panel synthesizes a placeholder). */
  overview: KbEntry | undefined;
  /** Total non-deleted entries across root + folders (for the "N sources" count). */
  entryCount: number;
  loading: boolean;
  error: string | null;
  reload: () => void;
  /** Upload a batch. folderId optional: absent = root, present = into that folder. */
  upload: (files: File[], folderId?: string) => Promise<void>;
  uploading: boolean;
  uploadError: string | null;
  remove: (fileId: string) => Promise<boolean>;
  deletingId: string | null;
  deleteError: string | null;
  download: (entry: KbEntry) => Promise<void>;
  downloadingId: string | null;
  downloadError: string | null;
  fetchTextContent: (entry: KbEntry) => Promise<string>;
  saveEdit: (entry: KbEntry, text: string) => Promise<boolean>;
  savingEditId: string | null;
  editError: string | null;
  /** Save the Overview markdown. First save (overview absent) = the 2-step upload
   *  flow; subsequent = the file edit flow. Reloads on success. Resolves true on ok. */
  saveOverview: (text: string) => Promise<boolean>;
  savingOverview: boolean;
  overviewError: string | null;
  /** Add a URL entry (a link, not a file). folderId optional (root when absent).
   *  Reloads on success. Resolves true on ok. */
  addUrl: (displayName: string, url: string, folderId?: string) => Promise<boolean>;
  /** Edit a URL entry (partial). Reloads on success. Resolves true on ok. */
  updateUrl: (fileId: string, fields: { displayName?: string; url?: string }) => Promise<boolean>;
  savingUrl: boolean;
  urlError: string | null;
  /** Clear urlError — call when opening/closing the URL form so a prior failure's message
   *  doesn't leak onto a freshly-opened (or different) form. */
  clearUrlError: () => void;
}

export function useKb(ownerType: KbOwnerType, ownerId: string | null): UseKb {
  const owner: KbOwner | null = useMemo(
    () => (ownerId ? { ownerType, ownerId } : null),
    [ownerType, ownerId],
  );

  const [rootEntries, setRootEntries] = useState<KbEntry[]>([]);
  const [folders, setFolders] = useState<KbTreeFolder[]>([]);
  const [overview, setOverview] = useState<KbEntry | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [savingEditId, setSavingEditId] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [savingOverview, setSavingOverview] = useState(false);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const [savingUrl, setSavingUrl] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  const clearUrlError = useCallback(() => setUrlError(null), []);

  useEffect(() => {
    // No owner (ownerId null / empty — e.g. a route param not resolved yet, or a shared
    // caller passing null): don't leave `loading` stuck true. Clear to an empty,
    // not-loading state so the panel shows its empty view rather than a perpetual spinner.
    if (!owner) {
      setLoading(false);
      setRootEntries([]);
      setFolders([]);
      setOverview(undefined);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const out = await getIvtClient().send(
          new GetKbTreeCommand({ ownerType: owner.ownerType, ownerId: owner.ownerId }),
        );
        if (cancelled) return;
        const allEntries = out.entries ?? [];
        const allFolders = out.folders ?? [];
        // Group entries under folders by folderId; folderId absent = root level.
        // Built client-side so folders light up with zero render change.
        const byFolder = new Map<string, KbEntry[]>();
        const roots: KbEntry[] = [];
        for (const e of allEntries) {
          if (e.folderId) {
            const list = byFolder.get(e.folderId);
            if (list) list.push(e);
            else byFolder.set(e.folderId, [e]);
          } else {
            roots.push(e);
          }
        }
        // Attach entries to their folder, CONSUMING them from byFolder as we go.
        const tree = allFolders.map((folder) => {
          const id = folder.folderId as string;
          const entries = byFolder.get(id) ?? [];
          byFolder.delete(id);
          return { folder, entries };
        });
        // Anything left in byFolder is an ORPHAN: its folderId points at a folder not in
        // folders[] (stale link — folder deleted, or a backend inconsistency). Surface it
        // at root instead of dropping it, so no file becomes invisible/unmanageable and
        // the source count stays honest.
        for (const orphaned of byFolder.values()) roots.push(...orphaned);
        setFolders(tree);
        setRootEntries(roots);
        setOverview(out.overview);
      } catch (e) {
        if (!cancelled) setError((e as Error).message ?? 'Unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [owner, reloadKey]);

  const entryCount = useMemo(
    () => rootEntries.length + folders.reduce((n, f) => n + f.entries.length, 0),
    [rootEntries, folders],
  );

  // Upload one file end-to-end (request → S3 PUT → confirm). Returns null on success or
  // `{ message, denialKind }`; never sets state / reloads, so a batch collects outcomes.
  // `denialKind` is carried separately so `upload` can state a shared authorization
  // failure ONCE instead of repeating the same ~80-char hint per file.
  const uploadOne = useCallback(
    async (file: File, folderId?: string): Promise<UploadFailure | null> => {
      const fail = (message: string): UploadFailure => ({ message });
      if (!owner) return fail(`「${file.name}」上传失败：缺少归属标识。`);
      const contentType = contentTypeForFile(file.name);
      if (!contentType) return fail(`「${file.name}」不支持的文件类型（仅 pdf / doc / docx / pptx / xls / xlsx / csv / html / txt / md）`);
      if (file.size > KB_MAX_FILE_BYTES) return fail(`「${file.name}」超过 ${KB_MAX_FILE_LABEL}，请压缩后重新上传。`);
      let step: 'request' | 'put' | 'confirm' = 'request';
      try {
        const req = await kbWriteOps.requestUploadUrl(owner, file.name, contentType, folderId);
        if (!req.uploadUrl || !req.fileId) throw new Error('no upload url');
        // Content-Type MUST equal the signed type; no other (esp. x-amz-*) header.
        step = 'put';
        const put = await fetch(req.uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': contentType } });
        if (!put.ok) throw new Error(`s3 put ${put.status}`);
        step = 'confirm';
        await kbWriteOps.confirmUpload(owner, req.fileId);
        return null;
      } catch (e) {
        const name = (e as { name?: string }).name ?? '';
        if (step === 'confirm' && name === 'InvalidInputException') {
          return fail(`「${file.name}」超过 ${KB_MAX_FILE_LABEL}，请压缩后重新上传。`);
        }
        // A 403 lands on the FIRST step (RequestKbFileUploadUrl), so the generic
        // "请重新上传" would be wrong advice for a permission problem that no number of
        // re-uploads can fix. Report the file name here (a mixed batch must stay
        // attributable) and let `upload` add the shared hint once.
        const kind = denialKindOf(e);
        if (kind) return { message: `「${file.name}」上传失败`, denialKind: kind };
        return fail(`「${file.name}」上传失败，请重新上传。`);
      }
    },
    [owner],
  );

  const upload = useCallback(
    async (files: File[], folderId?: string) => {
      if (files.length === 0) return;
      setUploading(true);
      setUploadError(null);
      const failures: UploadFailure[] = [];
      for (const file of files) {
        const err = await uploadOne(file, folderId);
        if (err) failures.push(err);
      }
      reload(); // single refresh after the whole batch
      // Every denial in one batch has the same cause (same caller, same op), so the hint
      // is appended ONCE at the end rather than per file — 4 files would otherwise repeat
      // the same ~80-char sentence four times and bury the file names.
      const sharedDenial = failures.find((f) => f.denialKind)?.denialKind;
      const lines = failures.map((f) => f.message);
      if (sharedDenial) lines.push(DENIAL_HINTS[sharedDenial]);
      setUploadError(lines.length ? lines.join('\n') : null);
      setUploading(false);
    },
    [uploadOne, reload],
  );

  const remove = useCallback(
    async (fileId: string): Promise<boolean> => {
      if (!owner) return false;
      setDeletingId(fileId);
      setDeleteError(null);
      try {
        await kbWriteOps.remove(owner, fileId);
        reload();
        return true;
      } catch (e) {
        setDeleteError(denialHint(e) ?? ((e as Error).message || '删除失败'));
        return false;
      } finally {
        setDeletingId(null);
      }
    },
    [owner, reload],
  );

  const download = useCallback(
    async (entry: KbEntry): Promise<void> => {
      if (!owner || !entry.fileId) return;
      setDownloadingId(entry.fileId);
      setDownloadError(null);
      try {
        const url = await kbWriteOps.downloadUrl(owner, entry.fileId, KbDownloadDisposition.ATTACHMENT);
        if (!url) throw new Error('no download url');
        // Presigned URL is a bearer token — use once via a transient <a download>;
        // never window.location / address bar / log it.
        const a = document.createElement('a');
        a.href = url;
        a.download = entry.displayName ?? '';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (e) {
        // RequestKbDownloadUrl is @readonly so any IVT member may call it — but the
        // membership / expired-session 403 still reaches here, and "请重试" is wrong
        // for that. (The presigned GET itself can also fail; that keeps the retry advice.)
        const denied = denialHint(e);
        setDownloadError(
          denied ?? `「${entry.displayName ?? ''}」下载失败，请重试。`,
        );
      } finally {
        setDownloadingId(null);
      }
    },
    [owner],
  );

  const fetchTextContent = useCallback(
    async (entry: KbEntry): Promise<string> => {
      if (!owner || !entry.fileId) throw new Error('no file id');
      const url = await kbWriteOps.downloadUrl(owner, entry.fileId, KbDownloadDisposition.INLINE);
      if (!url) throw new Error('no download url');
      const res = await fetch(url); // bearer token — fetch once, don't log
      if (!res.ok) throw new Error(`预览获取失败（${res.status}）`);
      return res.text();
    },
    [owner],
  );

  const saveEdit = useCallback(
    async (entry: KbEntry, text: string): Promise<boolean> => {
      if (!owner || !entry.fileId) return false;
      if (!isEditableFile(entry.displayName)) {
        setEditError('该文件类型不支持编辑（仅 txt / md 可编辑）。');
        return false;
      }
      // PUT Content-Type MUST equal the file's AUTHORITATIVE stored contentType, not one
      // re-derived from the display name (name and stored type are decoupled → deriving
      // from the extension can send the wrong type and trip S3 403). Fall back to the
      // name-derived type only if the entry somehow lacks one.
      const contentType = entry.contentType ?? contentTypeForFile(entry.displayName ?? '');
      if (!contentType) {
        setEditError('该文件类型不支持编辑（仅 txt / md 可编辑）。');
        return false;
      }
      setSavingEditId(entry.fileId);
      setEditError(null);
      try {
        const editUrl = await kbWriteOps.requestEditUrl(owner, entry.fileId);
        if (!editUrl) throw new Error('no edit url');
        const put = await fetch(editUrl, { method: 'PUT', body: text, headers: { 'Content-Type': contentType } });
        if (!put.ok) throw new Error(`s3 put ${put.status}`);
        await kbWriteOps.confirmEdit(owner, entry.fileId);
        reload();
        return true;
      } catch (e) {
        const name = (e as { name?: string }).name ?? '';
        setEditError(
          name === 'InvalidInputException'
            ? '该文件暂不可编辑（仅已上传的 txt / md 文件可编辑）。'
            : denialHint(e) ?? `「${entry.displayName ?? ''}」保存失败，请重试。`,
        );
        return false;
      } finally {
        setSavingEditId(null);
      }
    },
    [owner, reload],
  );

  // Save the Overview markdown. FIRST save (overview absent) = the dedicated 2-step
  // upload flow (RequestKbOverviewUploadUrl → PUT → ConfirmKbOverviewUpload); the
  // deterministic fileId comes back from step 1 (the client never invents one — a
  // placeholder node has no real id). SUBSEQUENT saves (overview exists) reuse the file
  // edit flow keyed by the real overview fileId. Overview is always markdown.
  const saveOverview = useCallback(
    async (text: string): Promise<boolean> => {
      if (!owner) return false;
      setSavingOverview(true);
      setOverviewError(null);
      const overviewContentType = 'text/markdown' as KbContentType;
      try {
        if (!overview) {
          // First save: mint the Overview upload URL (server returns the id), PUT, confirm.
          const req = await kbWriteOps.requestOverviewUploadUrl(owner);
          if (!req.uploadUrl || !req.fileId) throw new Error('no overview upload url');
          const put = await fetch(req.uploadUrl, {
            method: 'PUT',
            body: text,
            headers: { 'Content-Type': overviewContentType },
          });
          if (!put.ok) throw new Error(`s3 put ${put.status}`);
          await kbWriteOps.confirmOverviewUpload(owner, req.fileId);
        } else {
          // Subsequent: reuse the file edit flow keyed by the real overview fileId.
          if (!overview.fileId) throw new Error('overview has no fileId');
          const editUrl = await kbWriteOps.requestEditUrl(owner, overview.fileId);
          if (!editUrl) throw new Error('no edit url');
          const put = await fetch(editUrl, {
            method: 'PUT',
            body: text,
            headers: { 'Content-Type': overview.contentType ?? overviewContentType },
          });
          if (!put.ok) throw new Error(`s3 put ${put.status}`);
          await kbWriteOps.confirmEdit(owner, overview.fileId);
        }
        reload(); // re-fetch GetKbTree so overview flips from absent→present (or updates)
        return true;
      } catch (e) {
        // Four write ops feed this one catch (Request/Confirm × overview-upload and
        // file-edit); all four are write-gated, so classify before falling back.
        setOverviewError(denialHint(e) ?? '保存概述失败，请重试。');
        return false;
      } finally {
        setSavingOverview(false);
      }
    },
    [owner, overview, reload],
  );

  // Add a URL entry (link, not a file). The backend validates the scheme (http/https
  // only) + rejects control chars; a rejected scheme comes back InvalidInputException.
  const addUrl = useCallback(
    async (displayName: string, url: string, folderId?: string): Promise<boolean> => {
      if (!owner) return false;
      setSavingUrl(true);
      setUrlError(null);
      try {
        await kbWriteOps.addUrl(owner, displayName, url, folderId);
        reload();
        return true;
      } catch (e) {
        const name = (e as { name?: string }).name ?? '';
        setUrlError(
          name === 'InvalidInputException'
            ? '链接格式不正确（仅支持 http / https）。'
            : denialHint(e) ?? '保存链接失败，请重试。',
        );
        return false;
      } finally {
        setSavingUrl(false);
      }
    },
    [owner, reload],
  );

  // Edit a URL entry (partial). Same error mapping as addUrl.
  const updateUrl = useCallback(
    async (fileId: string, fields: { displayName?: string; url?: string }): Promise<boolean> => {
      if (!owner) return false;
      setSavingUrl(true);
      setUrlError(null);
      try {
        await kbWriteOps.updateUrl(owner, fileId, fields);
        reload();
        return true;
      } catch (e) {
        const name = (e as { name?: string }).name ?? '';
        setUrlError(
          name === 'InvalidInputException'
            ? '链接格式不正确（仅支持 http / https）。'
            : denialHint(e) ?? '保存链接失败，请重试。',
        );
        return false;
      } finally {
        setSavingUrl(false);
      }
    },
    [owner, reload],
  );

  return {
    rootEntries, folders, overview, entryCount, loading, error, reload,
    upload, uploading, uploadError,
    remove, deletingId, deleteError,
    download, downloadingId, downloadError,
    fetchTextContent,
    saveEdit, savingEditId, editError,
    saveOverview, savingOverview, overviewError,
    addUrl, updateUrl, savingUrl, urlError, clearUrlError,
  };
}
